import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { applicationsAPI } from '../../services/api';
import { Mail, Phone, Calendar, Paperclip } from 'lucide-react';

const ManageApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchApplications = useCallback(async () => {
    try {
      setErrorMessage('');
      const data = await applicationsAPI.getAll();
      setApplications(data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setErrorMessage(error.message || 'Failed to load applications. Please check admin permissions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleStatusChange = async (id, status) => {
    try {
      await applicationsAPI.updateStatus(id, status);
      setApplications((items) => items.map((item) => (item.id === id ? { ...item, status } : item)));
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Failed to update status');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Applications</h1>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">All Applications ({applications.length})</h2>
          
          {errorMessage ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              <p className="font-semibold">Unable to load applications.</p>
              <p className="mt-1 text-sm">{errorMessage}</p>
              <p className="mt-2 text-sm">
                Form submissions are saved in the Supabase <span className="font-mono">applications</span> table. If rows exist there, run the admin-domain SQL migration and sign in again.
              </p>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No applications received yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.id} className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{app.name}</h3>
                      <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full mt-2">
                        {app.status}
                      </span>
                    </div>
                    <div className="text-right space-y-2">
                      <div className="text-sm text-gray-500">
                        <Calendar className="inline w-4 h-4 mr-1" />
                        {new Date(app.created_at).toLocaleDateString()}
                      </div>
                      <select
                        value={app.status}
                        onChange={(e) => handleStatusChange(app.id, e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Mail className="w-4 h-4" />
                      <a href={`mailto:${app.email}`} className="hover:text-red-700">
                        {app.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone className="w-4 h-4" />
                      <span>{app.phone}</span>
                    </div>
                  </div>
                  
                  {app.record_type && (
                    <div className="mb-3">
                      <span className="text-sm font-semibold text-gray-600">Record Type: </span>
                      <span className="text-sm text-gray-800">{app.record_type}</span>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 p-4 rounded">
                    <p className="text-sm font-semibold text-gray-600 mb-2">Message:</p>
                    <p className="text-gray-700">{app.message}</p>
                  </div>

                  {app.attachments?.length > 0 && (
                    <div className="mt-4 rounded bg-gray-50 p-4">
                      <p className="mb-2 text-sm font-semibold text-gray-600">Attachments:</p>
                      <div className="flex flex-wrap gap-2">
                        {app.attachments.map((attachment, index) => (
                          attachment.url ? (
                            <a
                              key={attachment.path}
                              href={attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-red-700 hover:border-red-300 hover:bg-red-50"
                            >
                              <Paperclip className="h-4 w-4" />
                              <span className="max-w-56 truncate">{attachment.name || `Attachment ${index + 1}`}</span>
                            </a>
                          ) : (
                            <span
                              key={attachment.path}
                              className="inline-flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500"
                            >
                              <Paperclip className="h-4 w-4" />
                              <span className="max-w-56 truncate">{attachment.name || `Attachment ${index + 1}`}</span>
                            </span>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default ManageApplications;
